import { useState, useRef, useEffect } from 'react';
import type { ViewMode, DataTableColumn, NamedView } from './types';

interface DataTableToolbarProps<T> {
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;

  // View modes
  viewModes: ViewMode[];
  currentView: ViewMode;
  onViewChange: (mode: ViewMode) => void;

  // Columns
  columns: DataTableColumn<T>[];
  hiddenColumns: Set<string>;
  onToggleColumn: (column: string) => void;

  // Filters
  activeFilterCount: number;
  onClearFilters: () => void;

  // Named views
  savedViews?: NamedView[];
  activeViewId?: string;
  onSaveView?: (name: string) => void;
  onLoadView?: (viewId: string) => void;
  onDeleteView?: (viewId: string) => void;

  // Actions
  showRefresh?: boolean;
  onRefresh?: () => void;
  onReset?: () => void;
  toolbarActions?: React.ReactNode;
}

const VIEW_ICONS: Record<ViewMode, string> = {
  table: '☰',
  cards: '▦',
  kanban: '▥',
};

const VIEW_LABELS: Record<ViewMode, string> = {
  table: 'Table',
  cards: 'Cards',
  kanban: 'Kanban',
};

export function DataTableToolbar<T>({
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  viewModes,
  currentView,
  onViewChange,
  columns,
  hiddenColumns,
  onToggleColumn,
  activeFilterCount,
  onClearFilters,
  savedViews = [],
  activeViewId = 'default',
  onSaveView,
  onLoadView,
  onDeleteView,
  showRefresh,
  onRefresh,
  onReset,
  toolbarActions,
}: DataTableToolbarProps<T>) {
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const columnPickerRef = useRef<HTMLDivElement>(null);
  const viewMenuRef = useRef<HTMLDivElement>(null);

  // Close column picker on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target as Node)) {
        setShowColumnPicker(false);
      }
      if (viewMenuRef.current && !viewMenuRef.current.contains(event.target as Node)) {
        setShowViewMenu(false);
        setShowSaveDialog(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeViewName = activeViewId === 'default'
    ? 'Default View'
    : savedViews.find(v => v.id === activeViewId)?.name || 'Default View';

  const handleSaveView = () => {
    if (newViewName.trim() && onSaveView) {
      onSaveView(newViewName.trim());
      setNewViewName('');
      setShowSaveDialog(false);
      setShowViewMenu(false);
    }
  };

  return (
    <div className="flex items-center gap-4 px-4 py-3 bg-white border-b border-sage-200">
      {/* Search bar */}
      <div className="relative flex-1 max-w-md">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sage-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-10 pr-4 py-2 text-sm border border-sage-200 rounded-lg focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Named views dropdown */}
      {onSaveView && onLoadView && (
        <div className="relative" ref={viewMenuRef}>
          <button
            onClick={() => setShowViewMenu(!showViewMenu)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-sage-200 rounded-lg hover:bg-sage-50 transition-colors"
          >
            <svg className="w-4 h-4 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <span className="text-forest-900 max-w-[120px] truncate">{activeViewName}</span>
            <svg className="w-4 h-4 text-sage-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showViewMenu && (
            <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-xl shadow-lg border border-sage-200 py-2 z-50">
              {/* Default view */}
              <button
                onClick={() => {
                  onLoadView('default');
                  setShowViewMenu(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-sage-50 ${
                  activeViewId === 'default' ? 'bg-forest-50 text-forest-700' : 'text-forest-900'
                }`}
              >
                <span className="text-sm">Default View</span>
                {activeViewId === 'default' && (
                  <svg className="w-4 h-4 text-forest-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>

              {/* Saved views */}
              {savedViews.length > 0 && (
                <>
                  <div className="border-t border-sage-100 my-1" />
                  <div className="px-3 py-1.5 text-xs font-medium text-sage-500 uppercase tracking-wider">
                    Saved Views
                  </div>
                  {savedViews.map((view) => (
                    <div
                      key={view.id}
                      className={`group flex items-center justify-between px-3 py-2 hover:bg-sage-50 ${
                        activeViewId === view.id ? 'bg-forest-50' : ''
                      }`}
                    >
                      <button
                        onClick={() => {
                          onLoadView(view.id);
                          setShowViewMenu(false);
                        }}
                        className={`flex-1 text-left text-sm truncate ${
                          activeViewId === view.id ? 'text-forest-700 font-medium' : 'text-forest-900'
                        }`}
                      >
                        {view.name}
                      </button>
                      <div className="flex items-center gap-1">
                        {activeViewId === view.id && (
                          <svg className="w-4 h-4 text-forest-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {onDeleteView && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteView(view.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-sage-400 hover:text-red-500 transition-all"
                            title="Delete view"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Save new view */}
              <div className="border-t border-sage-100 my-1" />
              {showSaveDialog ? (
                <div className="px-3 py-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newViewName}
                      onChange={(e) => setNewViewName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveView()}
                      placeholder="View name..."
                      className="flex-1 px-2 py-1.5 text-sm border border-sage-200 rounded-lg focus:border-forest-500 focus:outline-none"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveView}
                      disabled={!newViewName.trim()}
                      className="px-3 py-1.5 text-sm bg-forest-700 text-white rounded-lg hover:bg-forest-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-forest-700 hover:bg-sage-50"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Save current view...
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* View mode toggle */}
      <div className="flex items-center gap-2">
        {viewModes.length > 1 && (
          <div className="flex rounded-lg border border-sage-200 overflow-hidden">
            {viewModes.map((mode) => (
              <button
                key={mode}
                onClick={() => onViewChange(mode)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  currentView === mode
                    ? 'bg-forest-700 text-white'
                    : 'bg-white text-sage-600 hover:bg-sage-50'
                }`}
                title={VIEW_LABELS[mode]}
              >
                <span className="mr-1">{VIEW_ICONS[mode]}</span>
                <span className="hidden sm:inline">{VIEW_LABELS[mode]}</span>
              </button>
            ))}
          </div>
        )}

        {/* Active filter indicator */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-1 px-2 py-1 text-sm text-forest-700 bg-forest-50 rounded-lg hover:bg-forest-100 transition-colors"
          >
            <span className="font-medium">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}</span>
            <span className="text-forest-500">×</span>
          </button>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Custom actions */}
        {toolbarActions}

        {/* Column picker */}
        <div className="relative" ref={columnPickerRef}>
          <button
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            className="p-2 text-sage-600 hover:bg-sage-100 rounded-lg transition-colors"
            title="Show/hide columns"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </button>

          {showColumnPicker && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-sage-200 py-2 z-50">
              <div className="px-3 py-1.5 text-xs font-medium text-sage-500 uppercase tracking-wider">
                Columns
              </div>
              {columns.filter((c) => !c.pinned).map((column) => (
                <label
                  key={column.key}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-sage-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(column.key)}
                    onChange={() => onToggleColumn(column.key)}
                    className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
                  />
                  <span className="text-sm text-forest-900">{column.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Refresh button */}
        {showRefresh && onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 text-sage-600 hover:bg-sage-100 rounded-lg transition-colors"
            title="Refresh data"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}

        {/* Reset button */}
        {onReset && (
          <button
            onClick={onReset}
            className="p-2 text-sage-600 hover:bg-sage-100 rounded-lg transition-colors"
            title="Reset table settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
